import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';

import { CalendarDaysIcon } from '@heroicons/react/24/outline';

import { Content } from 'types/Content';
import Header from 'common/components/Header/Header';
import useItem from 'common/hooks/useItem';
import useRouter from 'common/hooks/use-router';
import useList from 'common/hooks/useList';
import FilteredList from 'common/components/Table/FilteredList';

import AlertsContext from 'common/providers/alerts';
import useAuth from 'common/hooks/useAuth';
import QuestionItem from './QuestionItem';

function Questions() {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error: errorList1,
  } = useList<Content>('contents', {
    defaultSorts: ['updatedAt:DESC', 'createdAt:DESC'],
    defaultFilters: {
      isFaq: true,
    },
    populate: ['images', 'documents', 'authorOperator', 'author'],
  });

  useEffect(() => {
    if (errorList1) {
      setAlert(errorList1, 'danger');
    }
  }, [errorList1, setAlert]);

  const { removeItem } = useItem<Content>('questions', '');

  const { navigate } = useRouter();
  const { user } = useAuth();

  const handleNavigate = (id: any) => {
    navigate(`/questions/edit/${id}`);
  };

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, 'danger');
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const columns = useMemo(
    () => [
      {
        Header: t('forms.question'),
        filterName: 'title$containsi',
        filterLabel: t('filters.search'),
        columnClasses: 'md:flex-1',
      } /* 
      {
        Header: t('forms.author'),
        filterName: 'author][username]$containsi',
        filterLabel: t('filters.search'),
        columnClasses: 'md:flex-1',
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      }, */,
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-slate-500">
      <Header
        title={t('titlePages.questions')}
        subTitle={t('titleLegends.questions')}
        btnTitle={t('newItem.question')}
        btnSubtitle={t('newItem.questionLegend')}
        Icon={CalendarDaysIcon}
        addLink="/questions/add"
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={QuestionItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Questions;
